import React, { useState, useEffect } from 'react';
import { createOperatore, deleteUser, fetchOperatori, fetchSocieta, fetchUsers } from './operatoriApi';
import { useUser } from '../../context/usercontext';
import { Societa } from '../../models/models';

interface GestioneOperatoriProps {
  onBack: () => void;
}

interface Operatore {
  id: number;
  email: string;
  nome: string;
  cognome: string;
  tariffa: number;
  societa: string;
  id_societa: number;
  ruolo: 'admin' | 'user' | 'root';
}

type AlertType = {
  message: string;
  type: 'error' | 'success';
} | null;

const GestioneOperatori: React.FC<GestioneOperatoriProps> = ({ onBack }) => {
  const { user } = useUser();
  const [operatori, setOperatori] = useState<Operatore[]>([]);
  const [email, setEmail] = useState('');
  const [nome, setNome] = useState('');
  const [cognome, setCognome] = useState('');
  const [tariffa, setTariffa] = useState('');
  const [societa, setSocieta] = useState('');
  const [ruolo, setRuolo] = useState<'admin' | 'user' | 'root'>('user');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [alert, setAlert] = useState<AlertType>(null);
  const [societaOptions, setSocietaOptions] = useState<Societa[]>([]);
  const [societaLoaded, setSocietaLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingOperatori, setLoadingOperatori] = useState(false);

  const loadOperatori = async () => {
    setLoadingOperatori(true);
    try {
      const [operatoriData, usersData] = await Promise.all([fetchOperatori(), fetchUsers()]);
      const operatoriMap = new Map(operatoriData.map(op => [op.email.toLowerCase(), op]));

      const operatoriMapped: Operatore[] = usersData.map(userItem => {
        const operatore = operatoriMap.get(userItem.email.toLowerCase());
        const societa = societaOptions.find(s => s.id === (operatore?.id_societa ?? 0));
        return {
          id: operatore?.id ?? userItem.id,
          email: userItem.email,
          nome: operatore?.nome ?? '',
          cognome: operatore?.cognome ?? '',
          tariffa: Number(operatore?.tariffa ?? 0),
          societa: societa?.societa ?? '',
          id_societa: operatore?.id_societa ?? 0,
          ruolo: userItem.role,
        };
      });

      setOperatori(operatoriMapped);
    } catch (error: any) {
      showAlert(error.message || 'Errore nel caricamento operatori', 'error');
    } finally {
      setLoadingOperatori(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        const societaData = await fetchSocieta();
        setSocietaOptions(societaData);
      } catch (error: any) {
        showAlert(error.message || 'Errore nel caricamento societa', 'error');
      } finally {
        setSocietaLoaded(true);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (societaLoaded) {
      loadOperatori();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [societaLoaded]);

  const showAlert = (message: string, type: 'error' | 'success') => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 3000);
  };

  const campiCompilati = email && nome && cognome && tariffa && societa && ruolo;

  const aggiungiOperatore = async () => {
    if (!campiCompilati) {
      showAlert('Compila tutti i campi', 'error');
      return;
    }

    setLoading(true);
    try {
      const societaSelezionata = societaOptions.find(s => s.id === parseInt(societa));
      if (!societaSelezionata) {
        throw new Error('Società non valida');
      }

      // Chiama le API in sequenza: prima registra utente, poi aggiunge operatore
      await createOperatore(
        nome,
        cognome,
        email,
        parseFloat(tariffa),
        societaSelezionata.id,
        ruolo
      );

      await loadOperatori();
      showAlert('Operatore aggiunto con successo!', 'success');
      resetForm();
    } catch (error: any) {
      showAlert(error.message || 'Errore nell\'aggiunta operatore', 'error');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEmail('');
    setNome('');
    setCognome('');
    setTariffa('');
    setSocieta('');
    setRuolo('user');
  };

  const avviaModifica = (id: number) => {
    setEditingId(id);
  };

  const salvaModifica = (id: number) => {
    setEditingId(null);
    showAlert('Operatore modificato con successo!', 'success');
  };

  const annullaModifica = () => {
    setEditingId(null);
  };

  const aggiornaOperatore = (id: number, campo: keyof Operatore, valore: any) => {
    setOperatori(operatori.map(op => 
      op.id === id ? { ...op, [campo]: valore } : op
    ));
  };

  const isCurrentUser = (emailToCheck: string) => {
    return (user?.email || '').toLowerCase() === emailToCheck.toLowerCase();
  };

  const rimuoviOperatore = async (id: number, emailOperatore: string) => {
    if (isCurrentUser(emailOperatore)) {
      showAlert('Non puoi eliminare il tuo utente', 'error');
      return;
    }

    const confirmDelete = window.confirm(`Confermi la cancellazione dell'utente ${emailOperatore}?`);
    if (!confirmDelete) {
      return;
    }

    setLoading(true);
    try {
      await deleteUser(emailOperatore);
      await loadOperatori();
      showAlert('Operatore eliminato', 'success');
    } catch (error: any) {
      showAlert(error.message || 'Errore nella cancellazione operatore', 'error');
    } finally {
      setLoading(false);
    }

    if (editingId === id) {
      setEditingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-5">
      <div className="max-w-5xl mx-auto">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="p-8 text-white flex justify-between items-center" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <div>
              <h1 className="text-3xl font-bold mb-2">👥 Gestione Operatori</h1>
              <p className="opacity-90">Inserisci e modifica gli operatori</p>
            </div>
            <button
              onClick={onBack}
              className="px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg transition-all"
            >
              ← Indietro
            </button>
          </div>

          <div className="p-8">
            {alert && (
              <div className={`p-3 rounded-lg mb-5 flex items-center gap-2 ${alert.type === 'error' ? 'bg-red-50 border border-red-200 text-red-600' : 'bg-green-50 border border-green-200 text-green-600'}`}>
                <span>{alert.type === 'error' ? '❌' : '✅'}</span>
                <span>{alert.message}</span>
              </div>
            )}

            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4 pb-2 border-b-2">📋 Nuovo Operatore</h2>
              <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-blue-500">
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block mb-2 font-medium text-gray-600">📧 Email</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="operatore@email.com"
                      className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block mb-2 font-medium text-gray-600">👔 Ruolo</label>
                    <select
                      value={ruolo}
                      onChange={(e) => setRuolo(e.target.value as 'admin' | 'user' | 'root')}
                      className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                    >
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                      <option value="root">Root</option>
                    </select>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block mb-2 font-medium text-gray-600">👤 Nome</label>
                    <input
                      type="text"
                      value={nome}
                      onChange={(e) => setNome(e.target.value)}
                      placeholder="Nome"
                      className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block mb-2 font-medium text-gray-600">👤 Cognome</label>
                    <input
                      type="text"
                      value={cognome}
                      onChange={(e) => setCognome(e.target.value)}
                      placeholder="Cognome"
                      className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block mb-2 font-medium text-gray-600">💰 Tariffa (€/ora)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={tariffa}
                      onChange={(e) => setTariffa(e.target.value)}
                      placeholder="Es: 25.50"
                      className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block mb-2 font-medium text-gray-600">🏢 Società</label>
                    <select
                      value={societa}
                      onChange={(e) => setSocieta(e.target.value)}
                      className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                    >
                      <option value="">Seleziona società...</option>
                      {societaOptions.map((soc) => (
                        <option key={soc.id} value={soc.id}>{soc.societa}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <button
                  onClick={aggiungiOperatore}
                  disabled={!campiCompilati || loading}
                  className={`w-full p-3 rounded-lg text-white font-medium transition-all ${
                    campiCompilati && !loading
                      ? 'bg-green-500 hover:bg-green-600 cursor-pointer' 
                      : 'bg-gray-400 cursor-not-allowed opacity-60'
                  }`}
                >
                  {loading ? '⏳ Aggiunta in corso...' : '+ Aggiungi Operatore'}
                </button>
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4 pb-2 border-b-2">📊 Lista Operatori ({operatori.length})</h2>
              {loadingOperatori && (
                <p className="text-center text-gray-500 py-4">Caricamento operatori...</p>
              )}
              {operatori.length === 0 ? (
                <p className="text-center text-gray-400 py-8">Nessun operatore ancora inserito</p>
              ) : (
                <div className="space-y-3">
                  {operatori.map(op => (
                    <div key={op.id} className={`bg-gray-50 p-5 rounded-lg border-l-4 transition-all ${
                      editingId === op.id ? 'border-blue-400 bg-blue-50' : 'border-blue-500'
                    }`}>
                      {editingId === op.id ? (
                        <div className="space-y-4">
                          <div className="grid md:grid-cols-2 gap-4">
                            <div>
                              <label className="block mb-1 text-sm font-medium text-gray-600">📧 Email</label>
                              <input
                                type="email"
                                value={op.email}
                                onChange={(e) => aggiornaOperatore(op.id, 'email', e.target.value)}
                                className="w-full p-2 border-2 border-blue-300 rounded-lg focus:border-blue-500 focus:outline-none"
                              />
                            </div>
                            <div>
                              <label className="block mb-1 text-sm font-medium text-gray-600">👔 Ruolo</label>
                              <select
                                value={op.ruolo}
                                onChange={(e) => aggiornaOperatore(op.id, 'ruolo', e.target.value)}
                                className="w-full p-2 border-2 border-blue-300 rounded-lg focus:border-blue-500 focus:outline-none"
                              >
                                <option value="user">User</option>
                                <option value="admin">Admin</option>
                                <option value="root">Root</option>
                              </select>
                            </div>
                          </div>
                          <div className="grid md:grid-cols-2 gap-4">
                            <div>
                              <label className="block mb-1 text-sm font-medium text-gray-600">👤 Nome</label>
                              <input
                                type="text"
                                value={op.nome}
                                onChange={(e) => aggiornaOperatore(op.id, 'nome', e.target.value)}
                                className="w-full p-2 border-2 border-blue-300 rounded-lg focus:border-blue-500 focus:outline-none"
                              />
                            </div>
                            <div>
                              <label className="block mb-1 text-sm font-medium text-gray-600">👤 Cognome</label>
                              <input
                                type="text"
                                value={op.cognome}
                                onChange={(e) => aggiornaOperatore(op.id, 'cognome', e.target.value)}
                                className="w-full p-2 border-2 border-blue-300 rounded-lg focus:border-blue-500 focus:outline-none"
                              />
                            </div>
                          </div>
                          <div className="grid md:grid-cols-2 gap-4">
                            <div>
                              <label className="block mb-1 text-sm font-medium text-gray-600">💰 Tariffa</label>
                              <input
                                type="number"
                                step="0.01"
                                value={op.tariffa}
                                onChange={(e) => aggiornaOperatore(op.id, 'tariffa', parseFloat(e.target.value))}
                                className="w-full p-2 border-2 border-blue-300 rounded-lg focus:border-blue-500 focus:outline-none"
                              />
                            </div>
                            <div>
                              <label className="block mb-1 text-sm font-medium text-gray-600">🏢 Società</label>
                              <select
                                value={op.id_societa}
                                onChange={(e) => {
                                  const id = parseInt(e.target.value);
                                  const soc = societaOptions.find(s => s.id === id);
                                  aggiornaOperatore(op.id, 'id_societa', id);
                                  if (soc) aggiornaOperatore(op.id, 'societa', soc.societa);
                                }}
                                className="w-full p-2 border-2 border-blue-300 rounded-lg focus:border-blue-500 focus:outline-none"
                              >
                                {societaOptions.map((soc) => (
                                  <option key={soc.id} value={soc.id}>{soc.societa}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <div className="flex gap-2 justify-end">
                            <button
                              onClick={() => annullaModifica()}
                              className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-all text-sm"
                            >
                              ✕ Annulla
                            </button>
                            <button
                              onClick={() => salvaModifica(op.id)}
                              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all text-sm"
                            >
                              ✓ Salva
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between flex-wrap gap-3">
                          <div className="flex-1 min-w-[200px]">
                            <div className="font-bold text-lg">{op.nome} {op.cognome}</div>
                            <div className="text-sm text-gray-600">{op.email}</div>
                          </div>
                          <div className="flex-1 min-w-[150px]">
                            <div className="text-sm text-gray-600">Tariffa</div>
                            <div className="font-semibold text-green-600">€ {op.tariffa.toFixed(2)}/ora</div>
                          </div>
                          <div className="flex-1 min-w-[150px]">
                            <div className="text-sm text-gray-600">Società</div>
                            <div className="font-semibold">{op.societa}</div>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => avviaModifica(op.id)}
                              className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 transition-all text-sm"
                            >
                              ✏️
                            </button>
                            <button
                              onClick={() => rimuoviOperatore(op.id, op.email)}
                              disabled={loading || isCurrentUser(op.email)}
                              title={isCurrentUser(op.email) ? 'Non puoi cancellare il tuo utente' : 'Elimina utente'}
                              className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition-all text-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GestioneOperatori;